import * as React from "react";

import { cx } from "@/lib/tailwind-utils";

const capTrim = "[text-box:trim-both_cap_alphabetic]";

interface KbdProps extends React.ComponentPropsWithoutRef<"kbd"> {
  /**
   * If true, displays "⌘" on Mac and "Ctrl" on Windows/Linux.
   * Can also pass a string like "K" to display "⌘K" or "Ctrl+K".
   */
  command?: boolean | string;
}

const Kbd = React.forwardRef<HTMLElement, KbdProps>(
  ({ className, command, children, ...props }, ref) => {
    const [isMac, setIsMac] = React.useState(false);

    React.useEffect(() => {
      setIsMac(
        typeof navigator !== "undefined" &&
          (navigator.userAgent?.includes("Mac") ||
            /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)),
      );
    }, []);

    // Each label is its own flex item, trimmed to cap height and baseline, so
    // items-center centers the letters themselves. Untrimmed, the line box keeps
    // room for descenders below the baseline and caps ride high in the key.
    const content = React.useMemo(() => {
      if (!command) return <span className={capTrim}>{children}</span>;

      if (command === true) {
        return isMac ? (
          <span className={cx(capTrim, "text-body")}>⌘</span>
        ) : (
          <span className={capTrim}>Ctrl</span>
        );
      }

      // If command is a string like "K", combine with modifier
      return isMac ? (
        <>
          <span className={cx(capTrim, "mr-px text-body")}>⌘</span>
          <span className={capTrim}>{command}</span>
        </>
      ) : (
        <span className={capTrim}>{`Ctrl+${command}`}</span>
      );
    }, [command, isMac, children]);

    return (
      <kbd
        ref={ref}
        className={cx(
          "pointer-events-none inline-flex h-5 select-none items-center gap-1",
          "rounded border border-border bg-background-secondary px-1.5 shadow-sm",
          "font-mono text-tiny font-medium text-foreground-secondary",
          className,
        )}
        {...props}
      >
        {content}
      </kbd>
    );
  },
);
Kbd.displayName = "Kbd";

export { Kbd };
